<template>
  <div class="ia-container">
    <div class="ia-content">
      <SearchInfoHeaderComponent
        v-if="!hasPerformedSearch"
        :title="TEXT.title"
        :subtitle="TEXT.subtitle"
        :icon="ICON.ia"
      />
      <SearchIABoxQueryComponent @new-query="handleNewQuery" />
      <div
        v-if="showProgressBar"
        class="progress-bar-container"
      >
        <div class="info-container">
          <div class="info-container-item">
            <span class="info-container-item-text">{{ loadingText }}</span>
          </div>
          <div class="info-container-progress">
            <span class="progress-percentage">{{ Math.round(loadingProgress) }}% completado</span>
          </div>
        </div>
        <v-progress-linear
          :value="loadingProgress"
          rounded
          color="#59ac85"
          background-color="#E0E0E0"
          height="8"
        />
      </div>
      <SearchIAResultComponent v-if="!showProgressBar && hasSearchResults" :results="searchResults" />
      <SearchIANoResultComponent v-else-if="!showProgressBar && hasPerformedSearch && !hasSearchResults" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n-composable'
import { computed, ref, onUnmounted, watch } from 'vue'
import SearchInfoHeaderComponent from '@/modules/documentSearcher/components/SearchAdvanceIA/components/SearchInfoHeaderComponent.vue'
import SearchIAResultComponent from '@/modules/documentSearcher/components/SearchAdvanceIA/components/SearchIAResultComponent.vue'
import SearchIANoResultComponent from '@/modules/documentSearcher/components/SearchAdvanceIA/components/SearchIANoResultComponent.vue'
import SearchIABoxQueryComponent from '@/modules/documentSearcher/components/SearchAdvanceIA/components/SearchIaBoxQuery/SearchIABoxQueryComponent.vue'
import type { IAResult } from '@/modules/documentSearcher/types/SearchIATypes'
import { useSearchIAStore } from '@/modules/documentSearcher/stores/useSearchIAStore'
import { TrackerEvents, trackEvent } from '@/plugins/tracker'

const { t } = useI18n()
const searchIAStore = useSearchIAStore()

// DATA
const hasPerformedSearch = ref(false)
const loadingProgress = ref(0)
const showProgressBar = ref(false)
const loadingText = ref('')
let progressInterval: number | null = null
let hideProgressTimeout: number | null = null

const TEXT = {
  title: t('components.search_advance_ia.header_title') as string,
  subtitle: t('components.search_advance_ia.header_subtitle') as string,
  loadingText: t('components.search_advance_ia.loading_text') as string,
  loadingPhase1: t('components.search_advance_ia.loading_phase_1') as string,
  loadingPhase2: t('components.search_advance_ia.loading_phase_2') as string,
  loadingPhase3: t('components.search_advance_ia.loading_phase_3') as string,
  loadingPhase4: t('components.search_advance_ia.loading_phase_4') as string
}

loadingText.value = TEXT.loadingPhase1

const ICON = { ia: require('@/assets/images/svg/icon-ia.svg') }

// COMPUTED
const searchResults = computed<IAResult | null>(() => searchIAStore.searchIAResponse)
const isLoading = computed<boolean>(() => searchIAStore.isLoading)
const wasCanceled = computed<boolean>(() => searchIAStore.wasCanceled)
const hasSearchResults = computed<boolean>(() => {
  return !!searchResults.value?.generated_response
})

// METHODS
const handleNewQuery = () => {
  hasPerformedSearch.value = true
}

const updateLoadingText = (progress: number) => {
  if (progress < 15) {
    loadingText.value = TEXT.loadingPhase1
  } else if (progress < 45) {
    loadingText.value = TEXT.loadingPhase2
  } else if (progress < 70) {
    loadingText.value = TEXT.loadingPhase3
  } else {
    loadingText.value = TEXT.loadingPhase4
  }
}

const startProgressSimulation = () => {
  trackEvent(TrackerEvents.LOADING_RESULTS_AI_SEARCH, { search_id: searchIAStore.search_id })
  loadingProgress.value = 0
  loadingText.value = TEXT.loadingPhase1
  showProgressBar.value = true
  clearProgressInterval()
  clearHideProgressTimeout()

  const scheduleNextIncrement = () => {
    const progress = loadingProgress.value

    let increment: number
    let delay: number

    if (progress < 30) {
      // Fase 1: 0–30%, +2% cada 400–800ms (~9s)
      increment = 2
      delay = Math.floor(Math.random() * 401) + 400
    } else if (progress < 70) {
      // Fase 2: 30–70%, +1% cada 600–1000ms (~32s)
      increment = 1
      delay = Math.floor(Math.random() * 401) + 600
    } else if (progress < 90) {
      // Fase 3: 70–90%, +0.5% cada 800–1200ms (~19s)
      increment = 0.5
      delay = Math.floor(Math.random() * 401) + 800
    } else if (progress < 99) {
      // Fase 4: 90–99%, +0.1% cada 1000ms (muy lento, esperando)
      increment = 0.1
      delay = 1000
    } else {
      return
    }

    loadingProgress.value = Math.min(progress + increment, 99)
    updateLoadingText(loadingProgress.value)
    progressInterval = setTimeout(scheduleNextIncrement, delay)
  }

  scheduleNextIncrement()
}

const clearProgressInterval = () => {
  if (progressInterval) {
    clearTimeout(progressInterval)
    progressInterval = null
  }
}

const clearHideProgressTimeout = () => {
  if (hideProgressTimeout) {
    clearTimeout(hideProgressTimeout)
    hideProgressTimeout = null
  }
}

const completeProgress = () => {
  clearProgressInterval()

  // Si fue cancelado, ocultar inmediatamente
  if (wasCanceled.value) {
    showProgressBar.value = false
    return
  }

  // Animar suavemente hasta 100% en pasos de +2% cada 30ms
  const animateToComplete = () => {
    if (loadingProgress.value < 100) {
      loadingProgress.value = Math.min(loadingProgress.value + 2, 100)
      progressInterval = setTimeout(animateToComplete, 30)
    } else {
      progressInterval = null
      hideProgressTimeout = setTimeout(() => {
        showProgressBar.value = false
      }, 1000)
    }
  }

  animateToComplete()
}

// WATCHERS
watch(isLoading, (newValue, oldValue) => {
  if (newValue && !oldValue) {
    // Comenzó la carga
    startProgressSimulation()
  } else if (!newValue && oldValue) {
    // Terminó la carga
    completeProgress()
  }
})

onUnmounted(() => {
  searchIAStore.resetSearchIAResponse()
  clearProgressInterval()
  clearHideProgressTimeout()
  searchIAStore.resetSearchId()
})
</script>

<style lang="scss" scoped>
.ia-container {
  display: flex;
  justify-content: center;
  width: 100%;
}

.ia-content {
  max-width: 750px;
  width: 100%;
}

.full-width-alert {
  margin: $spacing-xs;
  width: auto;
}

.progress-bar-container {
  margin: $spacing-md $spacing-nule;
  padding: $spacing-md;
  border-radius: 12px;
  background-color: $neutral-white-000;

  .info-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: $spacing-xxs;

    &-item {
      display: flex;
      align-items: center;

      &-text {
        font-family: $inter-font;
        font-size: 12px;
        font-weight: 400;
        color: $neutral-grey-900;
      }
    }

    &-progress {
      .progress-percentage {
        font-family: $inter-font;
        font-size: 12px;
        font-weight: 400;
        color: $tertiary-seaGreen-100;
      }
    }
  }
}
</style>
